// frontend/src/pages/EnterpriseAdmin.jsx — Enterprise Core (standalone, Phase 42)
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, Database, Globe, Landmark, Network, Phone, CreditCard,
  ArrowLeftRight, ListOrdered, FileText, ShieldCheck, Archive,
  Clock, Settings, Terminal, ChevronLeft, RefreshCw, CheckCircle,
  AlertTriangle, XCircle, Server, Cpu, HardDrive, Zap, MemoryStick,
} from 'lucide-react';

// ── helpers ──────────────────────────────────────────────────────────────────
const tok = () => localStorage.getItem('dwi_token');

const handle401 = (e) => {
  if (e.response?.status === 401) {
    localStorage.removeItem('dwi_token');
    localStorage.removeItem('dwi_user');
    window.location.href = '/login';
  }
  throw e;
};
const get = (url, params) =>
  axios.get(url, { headers: { Authorization: `Bearer ${tok()}` }, params })
    .then(r => r.data).catch(handle401);

const post = (url, data) =>
  axios.post(url, data, { headers: { Authorization: `Bearer ${tok()}` } })
    .then(r => r.data).catch(handle401);

const fmtUGX = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1e9) return `UGX ${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `UGX ${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `UGX ${(n / 1e3).toFixed(1)}K`;
  return `UGX ${Math.round(n).toLocaleString()}`;
};
const fmtUSD = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Number(n).toFixed(2)}`;
};
const fmtNum = (n) => (n ?? 0).toLocaleString();
const fmtDate = (s) => s ? new Date(s).toLocaleString() : '—';
const fmtAgo = (s) => {
  if (!s) return '—';
  const diff = Date.now() - new Date(s).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ── UI primitives ─────────────────────────────────────────────────────────────
const Card = ({ children, className = '' }) => (
  <div className={`bg-[#0d1426] border border-white/10 rounded-2xl p-5 ${className}`}>
    {children}
  </div>
);

const Kpi = ({ label, value, sub, color = 'text-cyan-400', icon: Icon }) => (
  <Card>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-white/50 text-xs uppercase tracking-wider mb-1 truncate">{label}</p>
        <p className={`text-xl font-bold ${color} truncate`}>{value ?? '—'}</p>
        {sub && <p className="text-white/40 text-xs mt-1 truncate">{sub}</p>}
      </div>
      {Icon && <Icon className={`w-5 h-5 ${color} opacity-60 flex-shrink-0 ml-2`} />}
    </div>
  </Card>
);

const StatusDot = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
    {ok ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label || (ok ? 'OK' : 'Down')}
  </span>
);

const LoadBox = ({ loading, error, children }) => {
  if (loading) return <div className="flex items-center justify-center h-40 text-white/30 text-sm">Loading…</div>;
  if (error) return <div className="flex items-center justify-center h-40 text-red-400 text-sm">{error}</div>;
  return children;
};

const Row = ({ label, value, mono }) => (
  <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
    <span className="text-white/50 text-xs">{label}</span>
    <span className={`text-white/90 text-xs font-semibold ${mono ? 'font-mono' : ''} truncate max-w-[60%] text-right`}>{value ?? '—'}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-3">{children}</p>
);

// ── 1. System Health ──────────────────────────────────────────────────────────
function SystemHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await get('/api/operations/health'); setData(r.data); }
    catch (e) { setError(e.response?.data?.message || 'Failed to load health'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="CPU Load" value={`${data.cpu?.loadPct ?? '?'}%`} sub={`${data.cpu?.cores} cores · 1m: ${data.cpu?.load1}`} color={data.cpu?.loadPct > 80 ? 'text-red-400' : 'text-green-400'} icon={Cpu} />
              <Kpi label="Memory Used" value={`${data.memory?.heapUsed ?? '?'} MB`} sub={`Heap total: ${data.memory?.heapTotal} MB`} color="text-cyan-400" icon={MemoryStick} />
              <Kpi label="OS Memory" value={`${data.memory?.osUsedPct ?? '?'}%`} sub={`${data.memory?.osFree} MB free of ${data.memory?.osTotal} MB`} color={data.memory?.osUsedPct > 85 ? 'text-amber-400' : 'text-green-400'} icon={Server} />
              <Kpi label="Disk Used" value={data.disk?.pct ?? '?'} sub={`${data.disk?.used} used · ${data.disk?.free} free`} color="text-purple-400" icon={HardDrive} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <SectionTitle>Process Info</SectionTitle>
                <Row label="Process Uptime" value={data.uptime?.process} />
                <Row label="System Uptime" value={data.uptime?.system} />
                <Row label="Node.js" value={data.nodeVersion} mono />
                <Row label="Platform" value={data.platform} />
                <Row label="Hostname" value={data.hostname} mono />
                <Row label="Version" value={data.version} mono />
                <Row label="PID" value={data.pid} mono />
              </Card>
              <Card>
                <SectionTitle>User Stats</SectionTitle>
                <Row label="Total Users" value={fmtNum(data.users?.total)} />
                <Row label="Admin Accounts" value={fmtNum(data.users?.admins)} />
                <Row label="KYC Pending" value={fmtNum(data.users?.kycPending)} />
                <Row label="Database State" value={data.database?.state === 1 ? 'Connected' : 'Disconnected'} />
                <Row label="DB Status" value={data.database?.status} />
                <Row label="Last Checked" value={fmtDate(data.checkedAt)} />
              </Card>
            </div>
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 2. Database Health ────────────────────────────────────────────────────────
function DatabaseHealthTab() {
  const [health, setHealth] = useState(null);
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [h, p] = await Promise.all([
        get('/api/operations/health'),
        get('/api/operations/performance'),
      ]);
      setHealth(h.data);
      setPerf(p.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const latencyColor = (ms) => {
    if (!ms) return 'text-white/40';
    if (ms < 50) return 'text-green-400';
    if (ms < 200) return 'text-amber-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {health && perf && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="DB Status" value={health.database?.status === 'connected' ? 'Connected' : 'Down'} color={health.database?.state === 1 ? 'text-green-400' : 'text-red-400'} icon={Database} />
              <Kpi label="Query Latency" value={perf.dbLatencyMs ? `${perf.dbLatencyMs}ms` : '—'} sub={perf.dbStatus} color={latencyColor(perf.dbLatencyMs)} icon={Zap} />
              <Kpi label="Heap Used" value={`${perf.memory?.heapUsedMB ?? '?'} MB`} color="text-cyan-400" icon={MemoryStick} />
              <Kpi label="Process RSS" value={`${perf.memory?.rssMB ?? '?'} MB`} color="text-purple-400" icon={Server} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <SectionTitle>Connection Details</SectionTitle>
                <Row label="Driver State" value={`${health.database?.state} — ${health.database?.status}`} />
                <Row label="DB Latency" value={perf.dbLatencyMs ? `${perf.dbLatencyMs}ms (${perf.dbStatus})` : 'unknown'} />
                <Row label="Node.js PID" value={perf.pid} mono />
                <Row label="Node Version" value={perf.nodeVersion} mono />
                <Row label="Process Uptime" value={perf.uptimeSeconds ? `${Math.floor(perf.uptimeSeconds / 3600)}h ${Math.floor((perf.uptimeSeconds % 3600) / 60)}m` : '—'} />
              </Card>
              <Card>
                <SectionTitle>Activity</SectionTitle>
                <Row label="Transactions Last Hour" value={fmtNum(perf.activity?.txLastHour)} />
                <Row label="Transactions Today" value={fmtNum(perf.activity?.txToday)} />
                {perf.slowRequests?.length > 0 ? (
                  <div className="mt-3">
                    <p className="text-amber-400 text-xs mb-2">Slow Requests</p>
                    {perf.slowRequests.map((r, i) => (
                      <div key={i} className="text-xs text-white/60 font-mono">{r.path} — {r.duration}ms</div>
                    ))}
                  </div>
                ) : <Row label="Slow Requests" value="None detected" />}
              </Card>
            </div>
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 3. API Monitoring ─────────────────────────────────────────────────────────
function ApiMonitoringTab() {
  const [perf, setPerf] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, pay] = await Promise.all([
        get('/api/operations/performance'),
        get('/api/operations/payments'),
      ]);
      setPerf(p.data);
      setPayments(pay.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const txData = payments?.transactions ?? {};
  const wdData = payments?.withdrawals ?? {};

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {perf && payments && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="DB Latency" value={perf.dbLatencyMs ? `${perf.dbLatencyMs}ms` : '—'} sub={perf.dbStatus} color={perf.dbLatencyMs < 100 ? 'text-green-400' : 'text-amber-400'} icon={Zap} />
              <Kpi label="Tx Last Hour" value={fmtNum(perf.activity?.txLastHour)} color="text-cyan-400" icon={Activity} />
              <Kpi label="Tx Today" value={fmtNum(perf.activity?.txToday)} color="text-purple-400" icon={Activity} />
              <Kpi label="Process Uptime" value={perf.uptimeSeconds ? `${Math.floor(perf.uptimeSeconds / 3600)}h` : '—'} color="text-green-400" icon={Clock} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <SectionTitle>Transaction Stats (All Time)</SectionTitle>
                {Object.entries(txData).map(([status, v]) => (
                  <div key={status} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-xs capitalize">{status}</span>
                    <div className="text-right">
                      <span className="text-white/90 text-xs font-semibold">{fmtNum(v.count)} txns</span>
                      <span className="text-white/40 text-xs ml-2">({fmtUSD(v.amount)})</span>
                    </div>
                  </div>
                ))}
              </Card>
              <Card>
                <SectionTitle>Withdrawal Stats (All Time)</SectionTitle>
                {Object.entries(wdData).map(([status, v]) => (
                  <div key={status} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-xs capitalize">{status}</span>
                    <div className="text-right">
                      <span className="text-white/90 text-xs font-semibold">{fmtNum(v.count)} items</span>
                      <span className="text-white/40 text-xs ml-2">({fmtUSD(v.amount)})</span>
                    </div>
                  </div>
                ))}
                {perf.slowRequests?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <SectionTitle>Slow Endpoints</SectionTitle>
                    {perf.slowRequests.map((r, i) => (
                      <Row key={i} label={r.path} value={`${r.duration}ms`} mono />
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 4. Treasury Dashboard ─────────────────────────────────────────────────────
function TreasuryDashboardTab() {
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [d, s] = await Promise.all([
        get('/api/treasury/dashboard'),
        get('/api/treasury/summary'),
      ]);
      setDashboard(d.dashboard);
      setSummary(s.summary);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ACCOUNT_COLORS = {
    mtn_float: 'text-amber-400',
    airtel_float: 'text-red-400',
    yo_settlement: 'text-cyan-400',
    cash_vault: 'text-green-400',
    bank: 'text-blue-400',
    usdt_wallet: 'text-purple-400',
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {dashboard && summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Kpi label="Total Value (USD)" value={fmtUSD(dashboard.totalValueUSD)} color="text-cyan-400" icon={Landmark} />
              <Kpi label="Account Groups" value={Object.keys(dashboard.accounts ?? {}).length} color="text-purple-400" icon={Database} />
              <Kpi label="As of" value={fmtDate(dashboard.generatedAt)} color="text-white/60" />
            </div>

            {Object.entries(dashboard.accounts ?? {}).map(([type, accounts]) => (
              <Card key={type}>
                <SectionTitle>{type.replace(/_/g, ' ').toUpperCase()}</SectionTitle>
                <div className="space-y-2">
                  {(accounts ?? []).map(acc => (
                    <div key={acc._id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <div>
                        <p className="text-white/90 text-xs font-semibold">{acc.name}</p>
                        <p className="text-white/40 text-[10px] font-mono">{acc.code}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-bold ${ACCOUNT_COLORS[acc.accountType] ?? 'text-white/70'}`}>
                          {acc.currency === 'UGX' ? fmtUGX(acc.balance) : `${acc.currency} ${Number(acc.balance ?? 0).toFixed(4)}`}
                        </p>
                        {acc.balanceUSD > 0 && (
                          <p className="text-white/40 text-[10px]">{fmtUSD(acc.balanceUSD)}</p>
                        )}
                        {acc.frozenBalance > 0 && (
                          <p className="text-amber-400 text-[10px]">Frozen: {fmtUGX(acc.frozenBalance)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 5. Payment Gateway Manager ────────────────────────────────────────────────
function GatewayManagerTab() {
  const [providers, setProviders] = useState([]);
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, h] = await Promise.all([
        get('/api/gateway/providers'),
        get('/api/gateway/health'),
      ]);
      setProviders(p.providers ?? []);
      setHealthData(h.providers ?? []);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const healthFor = (name) => healthData.find(h => h.provider === name);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh Health
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {providers.map(p => {
          const h = healthFor(p.name);
          return (
            <Card key={p._id} className="mb-3">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-white font-bold text-sm">{p.displayName}</p>
                  <p className="text-white/40 text-[10px] font-mono mt-0.5">{p.name}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <StatusDot ok={p.active} label={p.active ? 'Active' : 'Disabled'} />
                  {h && <StatusDot ok={h.healthy} label={h.healthy ? `${h.latency}ms` : 'Unreachable'} />}
                </div>
              </div>
              <p className="text-white/50 text-xs mb-3">{p.description || h?.message}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="bg-white/5 rounded-lg p-2"><span className="text-white/40 block">Country</span><span className="text-white/90 font-semibold">{p.country}</span></div>
                <div className="bg-white/5 rounded-lg p-2"><span className="text-white/40 block">Currency</span><span className="text-white/90 font-semibold">{p.currency}</span></div>
                <div className="bg-white/5 rounded-lg p-2"><span className="text-white/40 block">Deposits</span><span className={p.depositEnabled ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>{p.depositEnabled ? 'Enabled' : 'Disabled'}</span></div>
                <div className="bg-white/5 rounded-lg p-2"><span className="text-white/40 block">Payouts</span><span className={p.payoutEnabled ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>{p.payoutEnabled ? 'Enabled' : 'Disabled'}</span></div>
              </div>
              {h?.message && (
                <p className={`text-xs mt-3 px-3 py-2 rounded-lg ${h.healthy ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {h.message}
                </p>
              )}
              <div className="mt-3">
                <p className="text-white/30 text-[10px] uppercase tracking-widest mb-1">Credential Keys</p>
                <div className="flex flex-wrap gap-1">
                  {(p.credentialKeys ?? []).map(k => (
                    <span key={k} className="bg-white/5 text-white/50 text-[10px] font-mono px-2 py-0.5 rounded">{k}</span>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </LoadBox>
    </div>
  );
}

// ── 6. MTN & Airtel Config ────────────────────────────────────────────────────
function MtnAirtelTab() {
  const [providers, setProviders] = useState([]);
  const [health, setHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, h] = await Promise.all([
        get('/api/gateway/providers'),
        get('/api/gateway/health'),
      ]);
      setProviders((p.providers ?? []).filter(x => ['mtn_momo', 'airtel_money'].includes(x.name)));
      setHealth((h.providers ?? []).filter(x => ['mtn_momo', 'airtel_money'].includes(x.provider)));
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <LoadBox loading={loading} error={error}>
        {providers.map(p => {
          const h = health.find(x => x.provider === p.name);
          return (
            <Card key={p._id} className="mb-3">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-bold">{p.displayName}</p>
                  <p className="text-white/40 text-xs">{p.description}</p>
                </div>
                <StatusDot ok={h?.healthy} label={h?.healthy ? 'Live' : 'Simulation'} />
              </div>
              {h?.message && (
                <div className={`text-xs px-3 py-2 rounded-xl mb-4 ${h.healthy ? 'bg-green-500/10 text-green-400' : 'bg-amber-500/10 text-amber-400'}`}>
                  {h.message}
                </div>
              )}
              <SectionTitle>Configuration</SectionTitle>
              {(p.credentialKeys ?? []).map(k => (
                <div key={k} className="flex items-center justify-between py-1.5 border-b border-white/5">
                  <span className="text-white/50 text-xs font-mono">{k}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${h?.healthy ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                    {h?.healthy ? 'Set' : 'Not configured'}
                  </span>
                </div>
              ))}
              <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-amber-400 text-xs font-semibold mb-1">Setup Instructions</p>
                <p className="text-white/50 text-xs">Set credentials in the VPS environment: <span className="font-mono text-cyan-400">sudo nano /home/dwi/app/.env</span></p>
                <p className="text-white/50 text-xs mt-1">Then restart: <span className="font-mono text-cyan-400">sudo pm2 restart dwi-fintech-api</span></p>
              </div>
            </Card>
          );
        })}
        {providers.length === 0 && !loading && (
          <div className="text-white/30 text-sm text-center py-12">No MTN/Airtel providers found</div>
        )}
      </LoadBox>
    </div>
  );
}

// ── 7. Yo! Payments Config ────────────────────────────────────────────────────
function YoPaymentsTab() {
  const [provider, setProvider] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [p, h] = await Promise.all([
        get('/api/gateway/providers'),
        get('/api/gateway/health'),
      ]);
      setProvider((p.providers ?? []).find(x => x.name === 'yo_payments') ?? null);
      setHealth((h.providers ?? []).find(x => x.provider === 'yo_payments') ?? null);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <LoadBox loading={loading} error={error}>
        {provider && (
          <>
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-white font-bold text-lg">{provider.displayName}</p>
                  <p className="text-white/40 text-xs">{provider.description}</p>
                </div>
                <div className="flex gap-2">
                  <StatusDot ok={provider.active} label={provider.active ? 'Active' : 'Disabled'} />
                  <StatusDot ok={health?.healthy} label={health?.healthy ? `Live · ${health.latency}ms` : 'Degraded'} />
                </div>
              </div>
              {health?.message && (
                <div className={`text-xs px-3 py-2 rounded-xl mb-4 font-semibold ${health.healthy ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                  {health.message}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Kpi label="Collections" value={health?.collections ? 'Supported' : 'No'} color={health?.collections ? 'text-green-400' : 'text-red-400'} />
                <Kpi label="Bulk Payments" value={health?.bulkPayments ? 'Supported' : 'No'} color={health?.bulkPayments ? 'text-green-400' : 'text-red-400'} />
                <Kpi label="Mode" value={health?.mode ?? '—'} color={health?.mode === 'live' ? 'text-green-400' : 'text-amber-400'} />
                <Kpi label="Priority" value={`#${provider.priority}`} color="text-cyan-400" />
              </div>
            </Card>
            <Card>
              <SectionTitle>Webhook & IPN</SectionTitle>
              <Row label="Webhook Enabled" value={provider.webhookEnabled ? 'Yes' : 'No'} />
              <Row label="IPN URL" value={provider.webhookUrl} mono />
              <Row label="Last Updated" value={fmtDate(provider.updatedAt)} />
            </Card>
            <Card>
              <SectionTitle>Required Credentials</SectionTitle>
              {(provider.credentialKeys ?? []).map(k => (
                <Row key={k} label={k} value="Configured (env)" mono />
              ))}
            </Card>
          </>
        )}
        {!provider && !loading && <div className="text-white/30 text-sm text-center py-12">Yo! Payments provider not found</div>}
      </LoadBox>
    </div>
  );
}

// ── 8. Exchange Engine ────────────────────────────────────────────────────────
function ExchangeEngineTab() {
  const [settings, setSettings] = useState([]);
  const [treasury, setTreasury] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [s, t] = await Promise.all([
        get('/api/exchange/settings'),
        get('/api/exchange/treasury'),
      ]);
      setSettings(s.settings ?? []);
      setTreasury(t.treasury ?? null);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bool = (v) => v === true || v === 'true' || v === 1;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <SectionTitle>Exchange Settings</SectionTitle>
            {settings.map(s => (
              <div key={s.key} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                <span className="text-white/50 text-xs font-mono">{s.key.replace('EXCHANGE_', '')}</span>
                <span className={`text-xs font-semibold font-mono ${typeof s.value === 'boolean' ? (s.value ? 'text-green-400' : 'text-red-400') : 'text-cyan-400'}`}>
                  {typeof s.value === 'boolean' ? (s.value ? 'ENABLED' : 'DISABLED') : String(s.value)}
                </span>
              </div>
            ))}
          </Card>
          <Card>
            <SectionTitle>Treasury Wallet Addresses</SectionTitle>
            {treasury && Object.entries(treasury).map(([network, addr]) => (
              <div key={network} className="mb-3">
                <p className="text-white/40 text-xs mb-1">{network}</p>
                {addr
                  ? <p className="text-cyan-400 text-xs font-mono break-all">{addr}</p>
                  : <p className="text-white/20 text-xs italic">Not configured</p>
                }
              </div>
            ))}
            <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
              <p className="text-cyan-400 text-xs">These are the treasury receiving addresses for USDT deposits from customers.</p>
            </div>
          </Card>
        </div>
      </LoadBox>
    </div>
  );
}

// ── 9. Queue Manager ──────────────────────────────────────────────────────────
function QueueManagerTab() {
  const [queue, setQueue] = useState([]);
  const [qTotal, setQTotal] = useState(0);
  const [txStats, setTxStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [q, p] = await Promise.all([
        get('/api/exchange/queue'),
        get('/api/operations/payments'),
      ]);
      setQueue(q.orders ?? []);
      setQTotal(q.total ?? 0);
      setTxStats(p.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Exchange Queue" value={fmtNum(qTotal)} sub="Pending orders" color={qTotal > 0 ? 'text-amber-400' : 'text-green-400'} icon={ListOrdered} />
          <Kpi label="Tx Completed" value={fmtNum(txStats?.transactions?.completed?.count)} color="text-green-400" />
          <Kpi label="Tx Failed" value={fmtNum(txStats?.transactions?.failed?.count)} color={txStats?.transactions?.failed?.count > 0 ? 'text-red-400' : 'text-white/40'} />
          <Kpi label="Withdrawals Paid" value={fmtNum(txStats?.withdrawals?.paid?.count)} color="text-cyan-400" />
        </div>

        <Card>
          <SectionTitle>Exchange Order Queue</SectionTitle>
          {queue.length === 0 ? (
            <div className="text-center py-8 text-white/30 text-sm flex flex-col items-center gap-2">
              <CheckCircle className="w-8 h-8 text-green-400/40" />
              Queue is clear — no pending orders
            </div>
          ) : (
            <div className="space-y-2">
              {queue.map(o => (
                <div key={o._id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div>
                    <p className="text-white/90 text-xs font-semibold">{o.type?.toUpperCase() ?? 'ORDER'} — {o.currency}</p>
                    <p className="text-white/40 text-[10px] font-mono">{o._id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-cyan-400 text-sm font-bold">{fmtUSD(o.amount)}</p>
                    <p className="text-white/40 text-[10px]">{fmtAgo(o.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </LoadBox>
    </div>
  );
}

// ── 10. Audit Logs ────────────────────────────────────────────────────────────
function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await get('/api/audit/logs', { limit: 50 });
      setLogs(r.items ?? r.logs ?? []);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const severityColor = (s) => ({
    info: 'text-cyan-400', warn: 'text-amber-400', error: 'text-red-400', critical: 'text-red-500',
  }[s] ?? 'text-white/50');

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {logs.length === 0 ? (
          <div className="text-center py-12 text-white/30 text-sm">No audit logs found</div>
        ) : (
          <div className="space-y-1.5">
            {logs.map(log => (
              <div key={log._id} className="flex items-start gap-3 p-3 bg-white/5 rounded-xl hover:bg-white/8 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className={`text-xs font-mono font-bold ${severityColor(log.severity)}`}>{log.action}</span>
                    {log.method && <span className="text-white/30 text-[10px]">{log.method}</span>}
                    {log.responseStatus && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${log.responseStatus < 300 ? 'bg-green-500/20 text-green-400' : log.responseStatus < 500 ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'}`}>
                        {log.responseStatus}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-white/30 text-[10px]">
                    {log.path && <span className="font-mono truncate">{log.path}</span>}
                    {log.ipAddress && <span>{log.ipAddress}</span>}
                    {log.duration && <span>{log.duration}ms</span>}
                  </div>
                </div>
                <span className="text-white/25 text-[10px] flex-shrink-0 whitespace-nowrap">{fmtAgo(log.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </LoadBox>
    </div>
  );
}

// ── 11. Security Center ───────────────────────────────────────────────────────
function SecurityCenterTab() {
  const [data, setData] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [s, c] = await Promise.all([
        get('/api/operations/security'),
        get('/api/config', { category: 'security' }),
      ]);
      setData(s.data);
      setConfigs(c.configs ?? []);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>
      <LoadBox loading={loading} error={error}>
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Total Users" value={fmtNum(data.stats?.totalUsers)} color="text-cyan-400" icon={ShieldCheck} />
              <Kpi label="New Users (24h)" value={fmtNum(data.stats?.newUsers24h)} color="text-green-400" />
              <Kpi label="Audit Events (24h)" value={fmtNum(data.stats?.auditEvents24h)} color="text-purple-400" />
              <Kpi label="Security Events" value={fmtNum(data.stats?.securityEvents)} color={data.stats?.securityEvents > 0 ? 'text-red-400' : 'text-green-400'} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <SectionTitle>Security Configuration</SectionTitle>
                {configs.map(c => (
                  <div key={c.key} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                    <span className="text-white/50 text-xs truncate max-w-[60%]">{c.label || c.key}</span>
                    <span className={`text-xs font-semibold ${typeof c.value === 'boolean' ? (c.value ? 'text-green-400' : 'text-red-400') : 'text-cyan-400'}`}>
                      {typeof c.value === 'boolean' ? (c.value ? 'ON' : 'OFF') : String(c.value)}
                    </span>
                  </div>
                ))}
              </Card>
              <Card>
                <SectionTitle>Recent Security Logs</SectionTitle>
                {data.securityLogs?.length > 0 ? (
                  data.securityLogs.slice(0, 10).map((log, i) => (
                    <div key={i} className="py-1.5 border-b border-white/5 last:border-0">
                      <p className="text-white/90 text-xs">{log.action}</p>
                      <p className="text-white/30 text-[10px]">{fmtDate(log.createdAt)}</p>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-24 gap-2 text-white/30">
                    <CheckCircle className="w-6 h-6 text-green-400/40" />
                    <span className="text-xs">No security events</span>
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 12. Backup & Restore ──────────────────────────────────────────────────────
function BackupRestoreTab() {
  const [backup, setBackup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState('');
  const [trigResult, setTrigResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await get('/api/operations/backup');
      setBackup(r.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const triggerBackup = async () => {
    setTriggering(true); setTrigResult('');
    try {
      const r = await post('/api/operations/backup/trigger', {});
      setTrigResult(r.data?.message ?? 'Backup triggered successfully');
      setTimeout(load, 3000);
    } catch (e) {
      setTrigResult(e.response?.data?.message ?? 'Backup failed');
    } finally { setTriggering(false); }
  };

  const backupLines = backup?.lastBackupList
    ?.split('\n')
    .filter(l => l.match(/backup_\d{8}/)) ?? [];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-end">
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <button
          onClick={triggerBackup}
          disabled={triggering}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors disabled:opacity-50"
        >
          <Archive className="w-3.5 h-3.5" />
          {triggering ? 'Backing up…' : 'Trigger Backup Now'}
        </button>
      </div>

      {trigResult && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl text-green-400 text-xs">{trigResult}</div>
      )}

      <LoadBox loading={loading} error={error}>
        {backup && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Kpi label="Status" value={backup.status ?? '—'} color={backup.status === 'available' ? 'text-green-400' : 'text-amber-400'} icon={Archive} />
              <Kpi label="Backup Dir" value={backup.backupDir} color="text-white/60" />
              <Kpi label="Total Size" value={backup.totalSize ?? '—'} color="text-cyan-400" />
            </div>

            <Card>
              <SectionTitle>Backup History</SectionTitle>
              {backupLines.length > 0 ? backupLines.map((line, i) => {
                const m = line.match(/backup_(\d{8})_(\d{6})/);
                const name = m ? `${m[1].slice(0,4)}-${m[1].slice(4,6)}-${m[1].slice(6,8)} at ${m[2].slice(0,2)}:${m[2].slice(2,4)}:${m[2].slice(4,6)}` : line.trim();
                return (
                  <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                    <CheckCircle className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                    <span className="text-white/70 text-xs">{name}</span>
                  </div>
                );
              }) : (
                <p className="text-white/30 text-xs py-4 text-center">No backup records found</p>
              )}
            </Card>

            <Card>
              <SectionTitle>Latest Git Commit</SectionTitle>
              <p className="text-cyan-400 text-xs font-mono">{backup.latestCommit}</p>
            </Card>
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 13. Scheduler / Cron Jobs ─────────────────────────────────────────────────
function SchedulerTab() {
  const [backup, setBackup] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [b, h] = await Promise.all([
        get('/api/operations/backup'),
        get('/api/operations/health'),
      ]);
      setBackup(b.data);
      setHealth(h.data);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const KNOWN_CRONS = [
    { name: 'Database Backup',        schedule: '0 2 * * *',   desc: 'Daily at 02:00 UTC',       status: 'active' },
    { name: 'IPN Retry Worker',       schedule: '*/5 * * * *', desc: 'Every 5 minutes',          status: 'active' },
    { name: 'Treasury Reconciliation',schedule: '0 1 * * *',   desc: 'Daily at 01:00 UTC',       status: 'active' },
    { name: 'AML Alert Scan',         schedule: '0 */6 * * *', desc: 'Every 6 hours',            status: 'active' },
    { name: 'Wallet Daily Reset',     schedule: '0 0 * * *',   desc: 'Midnight UTC (daily limit)',status: 'active' },
    { name: 'Notification Cleanup',   schedule: '0 3 * * 0',   desc: 'Weekly Sunday 03:00',       status: 'active' },
    { name: 'Exchange Rate Update',   schedule: '*/15 * * * *',desc: 'Every 15 minutes',          status: 'active' },
  ];

  const backupHistory = backup?.lastBackupList?.split('\n').filter(l => l.match(/backup_\d{8}/)) ?? [];

  return (
    <div className="space-y-4">
      <LoadBox loading={loading} error={error}>
        <Card>
          <SectionTitle>Scheduled Jobs</SectionTitle>
          <div className="space-y-2">
            {KNOWN_CRONS.map(c => (
              <div key={c.name} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div>
                  <p className="text-white/90 text-xs font-semibold">{c.name}</p>
                  <p className="text-white/40 text-[10px]">{c.desc}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-white/30 text-[10px] font-mono hidden md:inline">{c.schedule}</span>
                  <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-0.5 rounded-full font-semibold">Active</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {backupHistory.length > 0 && (
          <Card>
            <SectionTitle>Last Backup Runs</SectionTitle>
            {backupHistory.map((line, i) => {
              const m = line.match(/backup_(\d{8})_(\d{6})/);
              const dt = m ? `${m[1].slice(0,4)}-${m[1].slice(4,6)}-${m[1].slice(6,8)} ${m[2].slice(0,2)}:${m[2].slice(2,4)}` : line;
              return (
                <div key={i} className="flex items-center gap-2 py-1.5 border-b border-white/5 last:border-0">
                  <CheckCircle className="w-3 h-3 text-green-400" />
                  <span className="text-white/60 text-xs">{dt}</span>
                </div>
              );
            })}
          </Card>
        )}

        {health && (
          <Card>
            <SectionTitle>System Timing</SectionTitle>
            <Row label="Process Uptime" value={health.uptime?.process} />
            <Row label="System Uptime" value={health.uptime?.system} />
            <Row label="Process Seconds" value={fmtNum(health.uptime?.processSeconds)} />
            <Row label="Checked At" value={fmtDate(health.checkedAt)} />
          </Card>
        )}
      </LoadBox>
    </div>
  );
}

// ── 14. Environment Config ────────────────────────────────────────────────────
function EnvironmentConfigTab() {
  const [health, setHealth] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [gwHealth, setGwHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [h, c, g] = await Promise.all([
        get('/api/operations/health'),
        get('/api/config', { category: 'general' }),
        get('/api/gateway/health'),
      ]);
      setHealth(h.data);
      setConfigs(c.configs ?? []);
      setGwHealth(g.providers ?? []);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ENV_CHECKLIST = [
    { key: 'JWT_SECRET', label: 'JWT Secret', required: true },
    { key: 'MONGO_URI', label: 'MongoDB URI', required: true },
    { key: 'YO_USERNAME', label: 'Yo! Username', required: true },
    { key: 'YO_PASSWORD', label: 'Yo! Password', required: true },
    { key: 'MTN_MOMO_API_USER_ID', label: 'MTN MoMo User ID', required: false },
    { key: 'AIRTEL_CLIENT_ID', label: 'Airtel Client ID', required: false },
    { key: 'SMTP_HOST', label: 'SMTP Host (email)', required: false },
    { key: 'AT_API_KEY', label: 'Africa\'s Talking (SMS)', required: false },
    { key: 'FCM_SERVER_KEY', label: 'FCM Server Key (push)', required: false },
    { key: 'VAPID_PUBLIC_KEY', label: 'VAPID Public Key', required: false },
  ];

  const yoOk = gwHealth.find(g => g.provider === 'yo_payments')?.healthy;
  const mtnOk = gwHealth.find(g => g.provider === 'mtn_momo')?.healthy;
  const airtelOk = gwHealth.find(g => g.provider === 'airtel_money')?.healthy;

  const statusFor = (key) => {
    if (key === 'YO_USERNAME' || key === 'YO_PASSWORD') return yoOk;
    if (key.startsWith('MTN')) return mtnOk;
    if (key.startsWith('AIRTEL')) return airtelOk;
    return undefined;
  };

  return (
    <div className="space-y-4">
      <LoadBox loading={loading} error={error}>
        {health && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Kpi label="Node.js" value={health.nodeVersion} mono color="text-green-400" />
              <Kpi label="Platform" value={health.platform} color="text-cyan-400" />
              <Kpi label="Hostname" value={health.hostname} color="text-white/60" />
              <Kpi label="App Version" value={health.version} color="text-purple-400" />
            </div>

            <Card>
              <SectionTitle>Environment Variables Status</SectionTitle>
              <div className="grid md:grid-cols-2 gap-x-6">
                {ENV_CHECKLIST.map(item => {
                  const configured = statusFor(item.key);
                  return (
                    <div key={item.key} className="flex items-center justify-between py-1.5 border-b border-white/5">
                      <div>
                        <span className="text-white/70 text-xs">{item.label}</span>
                        {item.required && <span className="text-red-400 text-[10px] ml-1">*required</span>}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                        configured === true ? 'bg-green-500/20 text-green-400'
                        : configured === false ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-white/5 text-white/30'
                      }`}>
                        {configured === true ? 'Configured' : configured === false ? 'Missing' : 'Unknown'}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 text-xs">
                To update environment variables: <span className="font-mono">sudo nano /home/dwi/app/.env</span> then <span className="font-mono">sudo pm2 restart dwi-fintech-api</span>
              </div>
            </Card>

            {configs.length > 0 && (
              <Card>
                <SectionTitle>General Config Keys</SectionTitle>
                {configs.slice(0, 10).map(c => (
                  <Row key={c.key} label={c.label || c.key} value={typeof c.value === 'boolean' ? (c.value ? 'true' : 'false') : String(c.value)} mono />
                ))}
              </Card>
            )}
          </>
        )}
      </LoadBox>
    </div>
  );
}

// ── 15. Business Config Overview ──────────────────────────────────────────────
function BusinessConfigTab() {
  const [configs, setConfigs] = useState([]);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const CATS = ['general','wallet','investment','fees','kyc','notifications','security','treasury','agents','merchants','branding','limits'];

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await get('/api/config', category ? { category } : undefined);
      setConfigs(r.configs ?? []);
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const grouped = configs.reduce((acc, c) => {
    const cat = c.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="bg-[#0d1426] border border-white/10 rounded-xl px-3 py-2 text-xs text-white/80 outline-none focus:border-cyan-400"
        >
          <option value="">All Categories</option>
          {CATS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={load} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-cyan-400 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-white/30 text-xs ml-auto">{configs.length} keys</span>
      </div>

      <LoadBox loading={loading} error={error}>
        {Object.entries(grouped).map(([cat, items]) => (
          <Card key={cat} className="mb-3">
            <SectionTitle>{cat.toUpperCase()} ({items.length} keys)</SectionTitle>
            <div className="space-y-0">
              {items.map(c => (
                <div key={c.key} className="flex items-start justify-between py-1.5 border-b border-white/5 last:border-0 gap-2">
                  <div className="min-w-0">
                    <p className="text-white/70 text-xs font-mono truncate">{c.key}</p>
                    {c.label && <p className="text-white/30 text-[10px]">{c.label}</p>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {c.isReadOnly && <span className="text-amber-400 text-[10px]">RO</span>}
                    <span className={`text-xs font-semibold ${typeof c.value === 'boolean' ? (c.value ? 'text-green-400' : 'text-red-400') : 'text-cyan-400'} font-mono`}>
                      {typeof c.value === 'object' ? JSON.stringify(c.value).slice(0, 30) : String(c.value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {configs.length === 0 && !loading && (
          <div className="text-white/30 text-sm text-center py-12">No config found — try seeding defaults in Business Config</div>
        )}
      </LoadBox>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const TABS = [
  { id: 'health',    label: 'System Health',      icon: Cpu           },
  { id: 'database',  label: 'Database Health',    icon: Database      },
  { id: 'api',       label: 'API Monitoring',     icon: Activity      },
  { id: 'treasury',  label: 'Treasury',           icon: Landmark      },
  { id: 'gateways',  label: 'Gateway Manager',    icon: Network       },
  { id: 'mtn_airtel',label: 'MTN & Airtel',       icon: Phone         },
  { id: 'yo',        label: 'Yo! Payments',       icon: CreditCard    },
  { id: 'exchange',  label: 'Exchange Engine',    icon: ArrowLeftRight},
  { id: 'queue',     label: 'Queue Manager',      icon: ListOrdered   },
  { id: 'audit',     label: 'Audit Logs',         icon: FileText      },
  { id: 'security',  label: 'Security Center',    icon: ShieldCheck   },
  { id: 'backup',    label: 'Backup & Restore',   icon: Archive       },
  { id: 'scheduler', label: 'Scheduler',          icon: Clock         },
  { id: 'env',       label: 'Environment',        icon: Terminal      },
  { id: 'config',    label: 'Business Config',    icon: Settings      },
];

const TAB_COMPONENT = {
  health:     <SystemHealthTab />,
  database:   <DatabaseHealthTab />,
  api:        <ApiMonitoringTab />,
  treasury:   <TreasuryDashboardTab />,
  gateways:   <GatewayManagerTab />,
  mtn_airtel: <MtnAirtelTab />,
  yo:         <YoPaymentsTab />,
  exchange:   <ExchangeEngineTab />,
  queue:      <QueueManagerTab />,
  audit:      <AuditLogsTab />,
  security:   <SecurityCenterTab />,
  backup:     <BackupRestoreTab />,
  scheduler:  <SchedulerTab />,
  env:        <EnvironmentConfigTab />,
  config:     <BusinessConfigTab />,
};

export default function EnterpriseAdmin() {
  const [activeTab, setActiveTab] = useState('health');

  // Null-token guard — redirect to login if not authenticated
  const token = localStorage.getItem('dwi_token');
  if (!token) {
    window.location.replace('/login');
    return null;
  }

  const ActiveComponent = TAB_COMPONENT[activeTab];

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-white">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#0a0f1e]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-4 h-14 flex items-center gap-4">
          <button
            onClick={() => window.location.href = '/admin'}
            className="flex items-center gap-1.5 text-white/50 hover:text-cyan-400 transition-colors text-sm"
          >
            <ChevronLeft className="w-4 h-4" />
            Admin
          </button>
          <div className="w-px h-5 bg-white/10" />
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="font-bold text-white text-sm">Enterprise Core</span>
          </div>
          <span className="ml-auto text-white/20 text-xs hidden md:block">
            {TABS.find(t => t.id === activeTab)?.label}
          </span>
        </div>
      </div>

      <div className="max-w-screen-2xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-6 hide-scrollbar border-b border-white/5">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                activeTab === id
                  ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/30'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Active Tab Content */}
        <div key={activeTab}>
          {ActiveComponent}
        </div>
      </div>
    </div>
  );
}
